import React from 'react'
import {useNavigate} from 'react-router-dom'
import {Modal} from 'react-bootstrap'
import {useI18n} from '../Utils/_helpers/useI18n'
import {ProductPlan as OriginalProductPlan} from '../../services/cellcast.v2.types'
import './Style/QuotaLimitModal.scss'

export type ProductPlan = OriginalProductPlan

interface QuotaLimitModalProps {
  show: boolean
  onClose: () => void
  onUpgrade: () => void
  plans?: ProductPlan[]
  currentPlan?: ProductPlan
  currentUsage?: number
}

const QuotaLimitModal: React.FC<QuotaLimitModalProps> = ({
  show,
  onClose,
  onUpgrade,
  plans,
  currentPlan,
  currentUsage = 0,
}) => {
  useI18n()
  const navigate = useNavigate()
  const paidPlans = plans?.filter((plan) => plan.price > 0) || []
  const upgradePlan =
    paidPlans.find((plan) => plan.price > (currentPlan?.price || 0)) || paidPlans[0]
  const monthlyPrice = upgradePlan?.price
  const currentQuotaLimit = currentPlan?.quotaLimit || 0
  const usagePercentage = Math.min((currentUsage / currentQuotaLimit) * 100, 100)

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      backdrop='static'
      keyboard={false}
      dialogClassName='quota-limit-modal'
      size='lg'
    >
      <div className='modal-header'>
        <button
          type='button'
          className='btn-close interactive-close'
          onClick={onClose}
          aria-label='Close modal'
        ></button>
      </div>

      <div className='modal-body'>
        {/* Warning Icon */}
        <div className='warning-icon-container'>
          <div className='warning-icon interactive-warning'>
            <i className='bi bi-exclamation-triangle-fill'></i>
          </div>
        </div>

        <h2 className='modal-title responsive-title'>
          You've reached your {currentPlan?.name || 'current'} limit
        </h2>
        <p className='modal-subtitle responsive-subtitle'>
          You're using{' '}
          <span className='usage-highlight interactive-highlight'>
            {currentUsage.toLocaleString()} of {currentQuotaLimit.toLocaleString()}
          </span>{' '}
          available on your plan.
        </p>

        {/* Usage card */}
        <div className='usage-card interactive-usage-card'>
          <div className='usage-header'>
            <div className='usage-label'>Quota usage</div>
            <div className='usage-limit'>/ {currentQuotaLimit.toLocaleString()} available</div>
          </div>
          <div className='usage-stats'>
            <span className='usage-current'>{currentUsage.toLocaleString()} used</span>
          </div>
          <div className='progress'>
            <div
              className='progress-bar interactive-progress'
              role='progressbar'
              style={{width: `${usagePercentage}%`}}
              aria-valuenow={usagePercentage}
              aria-valuemin={0}
              aria-valuemax={100}
            ></div>
          </div>
        </div>

        <div className='upgrade-plan-card interactive-plan-card'>
          <div className='plan-header'>
            <span className='plan-name'>Upgrade Plan: {upgradePlan?.name || 'Enterprise'}</span>
            <span className='plan-price'>${monthlyPrice} / month</span>
          </div>
          <ul className='plan-features'>
            {(upgradePlan?.features || []).slice(0, 3).map((feature, idx) => (
              <li key={idx} className='feature-item interactive-feature'>
                <i className='bi bi-check-circle-fill check-icon-1'></i>
                {feature}
              </li>
            ))}
          </ul>
        </div>

        <div className='action-buttons'>
          <button
            className='btn-manage interactive-button'
            onClick={() => {
              if (onClose) onClose()
              navigate('/wallet-card/subscription')
            }}
            aria-label='View all plans and manage subscription'
          >
            View All Plans / Manage Plan
          </button>
        </div>

        {/* Secondary text */}
        <p className='secondary-text responsive-secondary'>
          Or manage existing cards to stay within your current plan
        </p>
      </div>
    </Modal>
  )
}

export default QuotaLimitModal
